/**
 * Chat Service
 * Handles communication with the AI chatbot Cloud Function
 */

import { auth } from '../firebase/config';

const RATE_LIMIT_KEY = 'chat_rate_limit';
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW_MS = 60 * 1000; // 1 minute

class ChatService {
  constructor() {
    this.endpoint = process.env.REACT_APP_CHAT_ENDPOINT ||
      'https://us-central1-hoanguyendalat-13911.cloudfunctions.net/chatWithBot';
  }

  /**
   * Check and enforce client-side rate limiting
   * @returns {boolean} true if rate limit exceeded
   */
  isRateLimited() {
    try {
      const stored = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || '[]');
      const now = Date.now();
      const recent = stored.filter(ts => now - ts < RATE_LIMIT_WINDOW_MS);
      return recent.length >= RATE_LIMIT_MAX;
    } catch {
      return false;
    }
  }

  /**
   * Record a message timestamp for rate limiting
   */
  recordMessage() {
    try {
      const stored = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || '[]');
      const now = Date.now();
      const recent = stored.filter(ts => now - ts < RATE_LIMIT_WINDOW_MS);
      recent.push(now);
      localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent));
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Send a message to the chatbot
   * @param {string} message - User message
   * @param {Array} history - Conversation history [{role, content}]
   * @param {string} language - 'en' or 'vi'
   * @param {boolean} isAdmin - Whether the user is an admin
   * @returns {Promise<{success: boolean, reply?: string, structuredData?: object, error?: string}>}
   */
  async sendMessage(message, history, language, isAdmin = false) {
    if (this.isRateLimited()) {
      return {
        success: false,
        error: language === 'vi'
          ? 'Bạn đã gửi quá nhiều tin nhắn. Vui lòng đợi một phút.'
          : 'Too many messages. Please wait a minute.'
      };
    }

    this.recordMessage();

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      // Attach Firebase ID token for admin verification (force refresh to avoid stale tokens)
      if (isAdmin && auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken(true);
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers,
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify({ message, history, language, isAdmin })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Surface admin auth failure clearly
        if (response.status === 403) {
          return {
            success: false,
            error: language === 'vi'
              ? 'Xác thực admin thất bại. Vui lòng đăng xuất rồi đăng nhập lại.'
              : 'Admin verification failed. Please sign out and sign in again.'
          };
        }
        throw new Error(data.error || 'Failed to get response');
      }

      return {
        success: true,
        reply: data.reply,
        structuredData: data.structuredData || null
      };
    } catch (error) {
      console.error('Chat service error:', error);
      return {
        success: false,
        error: language === 'vi'
          ? 'Không thể kết nối. Vui lòng thử lại sau.'
          : 'Connection failed. Please try again later.'
      };
    }
  }
}

export const chatService = new ChatService();
